'use strict'

import {useCallback} from 'react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from './site-config'
import {toBrandCode, useBrand} from '../contexts/brand-context'
import {buildScopedEnvVarName} from './ugc-flags'
import {
    filterForYouCategoryTree,
    isForYouCategory,
    resolveForYouCategoryColor
} from './fyp-utils'
import {
    FYP_LIMIT_NAMES,
    FOR_YOU_FLAG_NAME,
    FOR_YOU_MAX_PRODUCTS,
    FOR_YOU_PAGE_SIZE
} from '../constants'

// Brand-scoped: DYN_VICKY_FYP_LANDING_ENABLED / GRG_VICKY_FYP_LANDING_ENABLED.
export const isForYouEnabled = (appConfig, siteId, brandId) => {
    const brandCode = toBrandCode(brandId)
    if (!brandCode) return false

    const scopedName = buildScopedEnvVarName(brandCode, FOR_YOU_FLAG_NAME)
    return getSiteEnvVarValue(appConfig, scopedName, siteId) === 'true'
}

const getFypLimit = (appConfig, envVariable, siteId, fallback) => {
    const raw = getSiteEnvVarValue(appConfig, envVariable, siteId)
    const parsed = parseInt(raw, 10)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

export const getFypPageSize = (appConfig, siteId) =>
    getFypLimit(appConfig, FYP_LIMIT_NAMES.PAGE_SIZE, siteId, FOR_YOU_PAGE_SIZE)

export const getFypMaxProductCount = (appConfig, siteId) =>
    getFypLimit(appConfig, FYP_LIMIT_NAMES.MAX_PRODUCTS, siteId, FOR_YOU_MAX_PRODUCTS)

export const useForYouCategoryFilter = (itemsKey = 'categories') => {
    const {site} = useMultiSite() || {}
    const {brandId} = useBrand() || {}
    const {app: appConfig} = getConfig()
    const isEnabled = isForYouEnabled(appConfig, site?.id, brandId)

    return useCallback(
        (node) => filterForYouCategoryTree(node, itemsKey, isEnabled),
        [itemsKey, isEnabled]
    )
}

/**
 * Returns a getter that resolves the "For You" navigation colour for a category,
 * scoped to the current brand. Garage reads `c_fypGarageColor`, Dynamite reads
 * `c_fypDynamiteColor` — both authored on the category in Business Manager. The
 * getter returns `undefined` for any category that is not the "For You" entry
 * point, and otherwise a valid hex colour (black when the attribute is unset,
 * empty, or invalid).
 *
 * @returns {(category: Object) => (string|undefined)}
 */
export const useForYouNavColor = () => {
    const {brandId} = useBrand() || {}
    return useCallback(
        (category) =>
            isForYouCategory(category) ? resolveForYouCategoryColor(category, brandId) : undefined,
        [brandId]
    )
}
